import { effectiveMemberStatusBucket, hasApplicationReviewHold } from "./member-status";
import { RESIDENT_EVIDENCE_REASONS, type ResidentEvidence } from "./resident-evidence";

export type BroadcastLifecycleStatus = "active" | "moving_out" | "inactive" | "request" | "unknown";
export const BROADCAST_LIFECYCLE_LABELS: Record<BroadcastLifecycleStatus, string> = {
  active: "Active", moving_out: "Moving out", inactive: "Inactive (former)",
  request: "Request or not moved in", unknown: "Unknown",
};

export interface BroadcastReviewedRecipient { id: number; reviewKey: string }
export type MemberResidencyView = Pick<BroadcastRecipientReview,
  "residencyEvidence" | "residencyCheckState" | "group" | "eligible" | "defaultSelected" |
  "exclusionReason" | "warning" | "roomId" | "moveOutDate">;
export function memberResidencyView(row?: BroadcastRecipientReview): MemberResidencyView | null {
  if (!row) return null;
  const { residencyEvidence, residencyCheckState, group, eligible, defaultSelected, exclusionReason, warning, roomId, moveOutDate } = row;
  return { residencyEvidence, residencyCheckState, group, eligible, defaultSelected, exclusionReason, warning, roomId, moveOutDate };
}
export function residentEvidenceLabel(view?: MemberResidencyView | null): string {
  if (!view) return "Current residency: Unverified — no authorized current stay evidence.";
  const evidence = view.residencyEvidence === "local_current" ? "Local tenancy verified"
    : view.residencyEvidence === "verified_current" ? "Live presence verified"
    : view.exclusionReason ?? "Current membership unverified";
  return `Current residency: ${evidence}${view.warning ? ` · ${view.warning}` : ""}${view.group === "excluded" ? " · Historical / not current" : ""}`;
}

export interface BroadcastRecipientReview {
  id: number;
  name: string;
  status: string;
  userId: number;
  propertyId: number | null;
  propertyName: string | null;
  padsplitMemberId: string | null;
  roomNumber: string | null;
  propertyConflict: boolean;
  conflictingPropertyId: number | null;
  conflictingPropertyName: string | null;
  duplicateRoom: boolean;
  lifecycleStatus: BroadcastLifecycleStatus;
  moveOutDate: string | null;
  roomId: number | null;
  roomName: string | null;
  eligible: boolean;
  exclusionReason: string | null;
  statusSource: "padsplit" | "direct_member";
  lastSyncedAt: string | null;
  group: "current" | "needs_review" | "excluded";
  defaultSelected: boolean;
  warning: string | null;
  reviewKey: string;
  residencyEvidence?: ResidentEvidence;
  /** Directory selection only. Never proof of live membership or send permission. */
  locallySelectable?: boolean;
  residencyCheckState?: "confirmed" | "checking" | "unavailable" | "failed";
}

/** Date-only values are already calendar dates. Timestamp values use the
 * property's zone; UTC is the explicit safe fallback for absent/invalid zones. */
export function broadcastDateOnly(value: Date | string | null | undefined, timezone?: string | null): string | null {
  if (value == null) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const parsed = new Date(`${value}T00:00:00Z`);
    return Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value ? null : value;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  let formatter: Intl.DateTimeFormat;
  try { formatter = new Intl.DateTimeFormat("en-US", { timeZone: timezone || "UTC", year: "numeric", month: "2-digit", day: "2-digit" }); }
  catch { formatter = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", year: "numeric", month: "2-digit", day: "2-digit" }); }
  const parts = formatter.formatToParts(date);
  return `${parts.find(p => p.type === "year")!.value}-${parts.find(p => p.type === "month")!.value}-${parts.find(p => p.type === "day")!.value}`;
}

/** Presentation only: never changes global lifecycle/delivery rules. */
export function classifyBroadcastRecipient(
  member: Parameters<typeof effectiveMemberStatusBucket>[0] & {
    status?: string | null; bookingRequestRetiredAt?: Date | string | null; movedOutRetiredAt?: Date | string | null;
    moveInDate?: Date | string | null; moveOutDate?: Date | string | null;
  },
  row: Pick<BroadcastRecipientReview, "roomId" | "moveOutDate" | "propertyConflict" | "duplicateRoom" | "residencyEvidence" | "locallySelectable">,
  today: string,
  timezone?: string | null,
): Pick<BroadcastRecipientReview, "lifecycleStatus" | "eligible" | "exclusionReason" | "group" | "defaultSelected" | "warning"> {
  if (hasApplicationReviewHold(member)) {
    return {
      lifecycleStatus: "request", eligible: false, defaultSelected: false,
      exclusionReason: "Application — needs review. Upstream outcome has not been verified.",
      group: "needs_review", warning: null,
    };
  }
  const bucket = effectiveMemberStatusBucket(member);
  const lifecycleStatus: BroadcastLifecycleStatus = bucket === "moving_in" || bucket === "booking_request" ? "request"
    : bucket === "terminated" ? "inactive" : bucket;
  const exclusionReason = row.propertyConflict ? "Property links disagree — correct the resident's property before sending."
    : row.duplicateRoom ? "Multiple current residents share this room — correct the roster before sending."
    : lifecycleStatus === "inactive" ? "Former or inactive resident — not eligible for this broadcast."
    : lifecycleStatus === "request" ? "Request or not moved in — not eligible for this broadcast."
    : lifecycleStatus === "unknown" ? "Unknown lifecycle status — refresh or correct this resident before sending."
    : row.residencyEvidence !== "verified_current" && row.residencyEvidence !== "local_current" &&
      !(row.residencyEvidence === "unavailable" && row.locallySelectable) ? RESIDENT_EVIDENCE_REASONS[row.residencyEvidence ?? "unavailable"]
    : row.roomId == null ? "No room assigned — reconcile before selecting."
    : null;
  const warnings: string[] = [];
  const lifecycleWarning = lifecycleStatus === "moving_out"
    ? row.moveOutDate == null ? "Moving out — no move-out date on file."
      : row.moveOutDate < today ? "Move-out date passed — departure not confirmed." : "Moving out — review before selecting."
    : lifecycleStatus === "active" && row.moveOutDate != null ? "Active status has a move-out date — status needs review."
    : lifecycleStatus === "unknown" ? "Unknown lifecycle status — status needs review."
    : row.roomId == null && lifecycleStatus === "active" ? "No room assigned — review before selecting."
    : null;
  if (lifecycleWarning) warnings.push(lifecycleWarning);
  if (member.moveOutDate != null && row.moveOutDate == null) warnings.push("Invalid move-out date on file — status needs review.");
  const moveInDate = broadcastDateOnly(member.moveInDate, timezone);
  if (member.moveInDate != null && moveInDate == null) warnings.push("Invalid move-in date on file — status needs review.");
  else if (lifecycleStatus === "active" && moveInDate != null && moveInDate > today) warnings.push("Active status has a future move-in date — status needs review.");
  const warning = warnings.length ? warnings.join(" ") : null;
  const group = lifecycleStatus === "inactive" || lifecycleStatus === "request" ? "excluded"
    : lifecycleStatus === "active" && row.roomId != null && !warning && !exclusionReason ? "current" : "needs_review";
  return { lifecycleStatus, eligible: exclusionReason == null, exclusionReason, group, defaultSelected: group === "current", warning };
}
